import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { usePWAInstall } from "@/hooks/usePWAInstall";

const INSTALL_DISMISSED_KEY = "pwa-install-dismissed";
const CONNECTION_TOAST_ID = "connection-status";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI__" in window;
}

/**
 * Invisible component that manages PWA lifecycle UX:
 * - Shows a debounced toast when the user goes offline / comes back online
 * - Shows a dismissable install prompt (persisted so it won't re-show)
 *
 * Disabled entirely inside Tauri desktop builds.
 * Mount once near the app root.
 */
export function PWAManager() {
  // No PWA UX inside the native desktop shell
  if (isTauri()) return null;

  return <PWAManagerInner />;
}

function PWAManagerInner() {
  const isOnline = useOnlineStatus();
  const { canInstall, isInstalled, install } = usePWAInstall();
  const wasOnline = useRef(navigator.onLine);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // ── Online / Offline toasts (debounced to avoid flapping spam) ──
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (wasOnline.current === isOnline) return;
      wasOnline.current = isOnline;

      if (!isOnline) {
        toast.warning("You're offline", {
          description: "Some features may be unavailable until you reconnect.",
          duration: Infinity,
          id: CONNECTION_TOAST_ID,
        });
      } else {
        toast.dismiss(CONNECTION_TOAST_ID);
        toast.success("Back online", {
          duration: 3000,
          id: CONNECTION_TOAST_ID,
        });
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
  }, [isOnline]);

  // ── Install prompt (persisted dismissal) ──
  useEffect(() => {
    if (!canInstall || isInstalled) return;

    // Don't show again if user previously dismissed
    try {
      if (localStorage.getItem(INSTALL_DISMISSED_KEY)) return;
    } catch {
      /* storage unavailable */
    }

    const t = setTimeout(() => {
      toast("Install CloudCode", {
        description: "Add CloudCode to your home screen for quick access.",
        action: {
          label: "Install",
          onClick: () => void install(),
        },
        onDismiss: () => {
          try {
            localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
          } catch {
            /* ignore */
          }
        },
        duration: 8000,
        id: "pwa-install",
      });
    }, 3000);

    return () => clearTimeout(t);
  }, [canInstall, isInstalled, install]);

  // ── iOS "Add to Home Screen" guidance ──
  useEffect(() => {
    if (isInstalled) return;

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) &&
      !(window as Window & { MSStream?: unknown }).MSStream;
    const isStandalone =
      "standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone;

    if (!isIOS || isStandalone) return;

    // Don't re-show if dismissed
    try {
      if (localStorage.getItem(INSTALL_DISMISSED_KEY)) return;
    } catch {
      return;
    }

    const t = setTimeout(() => {
      toast("Add CloudCode to Home Screen", {
        description:
          'Tap the share button, then "Add to Home Screen" for the best experience.',
        duration: 10000,
        id: "pwa-install-ios",
        onDismiss: () => {
          try {
            localStorage.setItem(INSTALL_DISMISSED_KEY, "1");
          } catch {
            /* ignore */
          }
        },
      });
    }, 4000);

    return () => clearTimeout(t);
  }, [isInstalled]);

  return null;
}
